import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import SubHeader from "../../custom/sub-header/sub-header";
import ConfirmDialog from "../../dialog/confirm/confirm-dialog";
import useAuth from "../../../hooks/useAuth";
import AuthService from "../../../service/auth-service";
import UserService from "../../../service/user-service";
import { googleSignOut } from "../../../utils/google-identity";
import "./my-page-style.scss";

//生日顯示：後端存 "YYYY-MM-DD"，畫面上用 "YYYY.MM.DD"
const formatBirth = (d) => (d ? d.replaceAll("-", ".") : "");

//性別代碼轉文字（代碼定義見 models/user.js）
const GENDER_LABEL = { "0": "女性", "1": "男性", "2": "其他" };

//個人檔案
//  個人資料 + 帳號操作（登出、註銷）。已被 RequireUserAuth 包住。
//
//  ⚠️ 畫面上的標題是「個人檔案」，但路由與檔名還是 my-page ——
//  改路由會動到 App.js、底部頁籤的 active 判斷、帳號中心的連結，
//  而那些都不是使用者看得到的字。改標題不必連帶改它們。
const MyPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [confirm, setConfirm] = useState(null); //null / "logout" / "deactivate"
  const [busy, setBusy] = useState(false);

  //副標：生日 · 性別。兩者都可能還沒填（Google 登入拿不到），
  //  所以要能只顯示其中一個，或都沒有時顯示補齊提示。
  const meta = [formatBirth(user?.userBirthDate), GENDER_LABEL[user?.userGender]]
    .filter(Boolean)
    .join(" · ");

  const handleLogout = async () => {
    //關掉 Google 的自動選取，否則下次進站會自動用同一個帳號登回去、
    //使用者會覺得「登出沒有用」
    googleSignOut();
    AuthService.logout();
    navigate("/", { replace: true });
  };

  const handleDeactivate = async () => {
    setBusy(true);
    try {
      await UserService.deactivate();
      googleSignOut();
      AuthService.logout();
      navigate("/", { replace: true });
    } catch (e) {
      setBusy(false);
      setConfirm(null);
    }
  };

  return (
    <main className="my">
      <SubHeader title="個人檔案" fallback="/account" />

      <section className="my__card">
        <div className="my__card-text">
          <h2 className="my__name">{user?.userName || "未命名"}</h2>
          {meta ? (
            <p className="my__meta">{meta}</p>
          ) : (
            //沒有生日性別時不要留白：直接給補齊的入口，
            //命理服務沒有這兩項算不出結果
            <Link to="/personal-editor" className="my__meta my__meta--empty">
              尚未填寫生日與性別，前往補齊
            </Link>
          )}
        </div>

        <Link
          to="/personal-editor"
          className="my__card-more"
          aria-label="編輯個人資料"
        >
          {/* 筆，不是三個點。三個點在手機上的慣例是「還有更多選項」——
              按下去應該跳一張選單，而這一顆是直接跳去編輯頁。
              圖示跟行為對不上，使用者會猶豫要不要按。 */}
          <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
            <path
              d="M4 20h4L19 9a2.1 2.1 0 00-3-3L5 17z"
              stroke="currentColor"
              strokeWidth="1.7"
              fill="none"
              strokeLinejoin="round"
            />
            {/* 筆桿與筆尖之間那一道，少了它在小尺寸下會看成一片三角形 */}
            <path
              d="M14.5 7.5L17 10"
              stroke="currentColor"
              strokeWidth="1.7"
              strokeLinecap="round"
            />
          </svg>
        </Link>
      </section>

      <button
        type="button"
        className="my__row"
        onClick={() => setConfirm("logout")}
      >
        <span className="my__row-title">登出</span>
        <IconChevron />
      </button>

      <button
        type="button"
        className="my__row"
        onClick={() => setConfirm("deactivate")}
      >
        <span className="my__row-title">註銷帳號</span>
        <IconChevron />
      </button>

      {/* 2026-10-28 拿掉這一頁底下的「服務條款 / 隱私權政策」（Kevin）。
          它們原本指向 /policy，而那條 route 不存在 —— 點下去是白頁。
          兩份條文仍然看得到：頁尾那兩個連結開的是 policy-dialog，
          全站的法律條文都在那一支裡。 */}

      <ConfirmDialog
        open={confirm === "logout"}
        title="登出"
        message="確定要登出嗎？下次仍可用同一個 Google 帳號登入。"
        confirmTxt="登出"
        cancelTxt="取消"
        onClose={() => setConfirm(null)}
        onSuccess={handleLogout}
      />

      <ConfirmDialog
        open={confirm === "deactivate"}
        title="註銷帳號"
        //講清楚後果：註銷是不可逆的操作，模糊的說明會讓人誤按
        message="註銷後將無法再登入，已購買的報告也會一併無法查看。確定要繼續嗎？"
        confirmTxt={busy ? "處理中…" : "確定註銷"}
        cancelTxt="取消"
        onClose={() => (busy ? null : setConfirm(null))}
        onSuccess={busy ? () => {} : handleDeactivate}
      />
    </main>
  );
};

const IconChevron = () => (
  <svg className="my__chevron" viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
    <path d="M9 5l7 7-7 7" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

export default MyPage;
